import { useAtom } from "jotai";
import { useState } from "react";

import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import {
  brushTypeAtom,
  eraseBrushSizeAtom,
  type BrushType
} from "@design-lab/widgets/artboard/model/state";

import { cn, Tabs, TabsList, TabsTrigger } from "@design-lab/shared";
import { useShowManualBgRemovalBrushSize } from "@design-lab/shared/lib/config";
import { Tooltip, TooltipContent, TooltipTrigger } from "@design-lab/shared/ui/tooltip";
import { RefineBrushSizeSlider } from "../RefineBrushSizeSlider";

type EraseToolProps = {
  isActive: boolean;
};

type BrushOption = {
  type: BrushType;
  label: string;
  testId: string;
  tooltipText: string;
  tooltipStorageKey: string;
};

const BRUSH_OPTIONS: BrushOption[] = [
  {
    type: "quick-selection",
    label: "Erase with a Click",
    testId: "quick-selection-brush-button",
    tooltipText:
      "Click to remove an area—similar colors around your selection will be erased automatically.",
    tooltipStorageKey: "quick-selection-brush-tooltip-seen"
  },
  {
    type: "manual",
    label: "Manual Brush",
    testId: "manual-brush-button",
    tooltipText: "Brush over the image to erase only the exact spots you cover.",
    tooltipStorageKey: "manual-brush-tooltip-seen"
  }
];

export function EraseTool({ isActive }: EraseToolProps) {
  const { isDesktop } = useResponsive();
  const [brushSize, setBrushSize] = useAtom(eraseBrushSizeAtom);
  const [brushType, setBrushType] = useAtom(brushTypeAtom);
  const shouldShowBrushSizeSlider = useShowManualBgRemovalBrushSize();

  const [brushTypeTooltipSeen, setBrushTypeTooltipSeen] = useState<Record<BrushType, boolean>>(
    () => {
      const seenState: Record<BrushType, boolean> = {} as Record<BrushType, boolean>;
      BRUSH_OPTIONS.forEach(option => {
        seenState[option.type] = localStorage.getItem(option.tooltipStorageKey) === "true";
      });
      return seenState;
    }
  );

  if (!isActive) return null;

  if (!shouldShowBrushSizeSlider) return null;

  const handleChangeTab = (newBrushType: BrushType) => {
    markTooltipAsSeen(brushType);
    setBrushType(newBrushType);
  };

  const markTooltipAsSeen = (optionType: BrushType) => {
    setBrushTypeTooltipSeen((prev: Record<BrushType, boolean>) => ({
      ...prev,
      [optionType]: true
    }));
    const brushOption = BRUSH_OPTIONS.find(option => option.type === optionType);
    if (brushOption) localStorage.setItem(brushOption.tooltipStorageKey, "true");
  };

  const shouldShowTooltip = (optionType: BrushType) =>
    brushType === optionType && !brushTypeTooltipSeen[optionType];

  return (
    <div
      className={cn(
        "bg-background absolute left-1/2 z-10 flex w-80 -translate-x-1/2 flex-col rounded-lg border border-gray-200 shadow-lg",
        isDesktop ? "bottom-22" : "bottom-26"
      )}
    >
      <Tabs
        value={brushType}
        onValueChange={(type: string) => handleChangeTab(type as BrushType)}
        className="w-full"
      >
        <TabsList className="h-11 rounded-none rounded-t-lg bg-inherit">
          {BRUSH_OPTIONS.map(option => (
            <Tooltip key={option.type} open={shouldShowTooltip(option.type)}>
              <TabsTrigger
                value={option.type}
                data-testid={option.testId}
                className="h-9 text-xs text-gray-700 data-[state=active]:bg-blue-700 data-[state=active]:text-white"
              >
                <TooltipTrigger asChild data-testid={`${option.testId}-tooltip`}>
                  <span>{option.label}</span>
                </TooltipTrigger>
              </TabsTrigger>
              <TooltipContent
                side="top"
                className="mx-3 max-w-xs text-pretty bg-blue-700 text-sm"
                onClose={() => markTooltipAsSeen(option.type)}
                sideOffset={8}
                arrowClassName="bg-blue-700 fill-blue-700"
              >
                {option.tooltipText}
              </TooltipContent>
            </Tooltip>
          ))}
        </TabsList>
      </Tabs>
      {brushType === "manual" && (
        <RefineBrushSizeSlider brushSize={brushSize} onBrushSizeChange={setBrushSize} />
      )}
    </div>
  );
}
